// TimerPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db } from "../firebase";
import { collection, addDoc, updateDoc, doc, onSnapshot, getDoc, setDoc, runTransaction, arrayUnion, deleteDoc, query, where } from 'firebase/firestore'; // Added deleteDoc here.
import RacerModal from './RacerModal';
import RunModal from './RunModal';
import { useAuth } from "../contexts/AuthContext";
import { auth } from '../firebase';
import { signInAnonymously } from "firebase/auth";
import { FaArrowLeft, FaClone, FaFlag, FaFlagCheckered, FaTimes, FaTrash, FaShareAlt, FaPlus, FaExchangeAlt, FaUndo } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import creditCosts from "../config/creditCosts";
import { QRCodeCanvas } from 'qrcode.react';
import { useToast } from "../components/Toast";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import FilterDropdown from "./FilterDropdown";

const TimerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const [run, setRun] = useState(null);
  const [showRacerModal, setShowRacerModal] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [inviteAdminEmail, setInviteAdminEmail] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [guestRole, setGuestRole] = useState(null); // "admin" | "viewer" | null
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [editingRunName, setEditingRunName] = useState(false);
  const [runNameEdit, setRunNameEdit] = useState("");
  const [editingTrack, setEditingTrack] = useState(false);
  const [trackEdit, setTrackEdit] = useState("");
  const [availableTracks, setAvailableTracks] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [processingRacerIds, setProcessingRacerIds] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [allRacers, setAllRacers] = useState([]);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapTargetRacer, setSwapTargetRacer] = useState(null);
  const [swapSelectedRacerId, setSwapSelectedRacerId] = useState(null);
  const [swapSearchTerm, setSwapSearchTerm] = useState("");
  const [finishWithoutStartConfirm, setFinishWithoutStartConfirm] = useState(null); // { racerId: string, finishTime: number } | null
  const [deleteRunConfirm, setDeleteRunConfirm] = useState(null); // { runId: string, runName: string } | null

  const handleOpenRacer = (event) => {
    const selectedRacerId = event.currentTarget.dataset.racerId;
    if (selectedRacerId) {
      navigate(`/racer/${selectedRacerId}`);
    }
  };

  const racerGroupsMap = useMemo(() => {
    const map = {};
    allRacers.forEach((racer) => {
      map[racer.id] = Array.isArray(racer.groups) ? racer.groups : [];
    });
    return map;
  }, [allRacers]);

  const availableGroups = useMemo(() => {
    if (!run || !run.racers) return [];
    const groups = new Set();
    run.racers.forEach((racer) => {
      const racerGroups = racerGroupsMap[racer.racerId] || [];
      racerGroups.forEach((group) => {
        if (group && group.trim()) {
          groups.add(group.trim());
        }
      });
    });
    return Array.from(groups).sort();
  }, [run, racerGroupsMap]);

  const filterRacersByGroup = (racers) => {
    if (!selectedGroups.length) return racers;
    return racers.filter((racer) => {
      const groups = racerGroupsMap[racer.racerId] || [];
      return groups.some((group) => selectedGroups.includes(group));
    });
  };

  const toggleGroup = (group) => {
    setSelectedGroups((prev) => {
      if (prev.includes(group)) {
        return prev.filter((g) => g !== group);
      }
      return [...prev, group];
    });
  };

  const clearSelectedGroups = () => {
    setSelectedGroups([]);
  };

  // Group filter dropdown uses shared FilterDropdown component
const generateRandomKey = () => {
  return Math.random().toString(36).substr(2, 8); // 8-character random key
};

const markRacerProcessing = (racerId) => {
  setProcessingRacerIds((prev) => [...prev, racerId]);
};

const unmarkRacerProcessing = (racerId) => {
  setProcessingRacerIds((prev) => prev.filter((id) => id !== racerId));
};

const updateRunRacers = async (mutateRacers) => {
  if (!run || !run.id) return null;
  const runRef = doc(db, "events", run.id); // TODO: After Firebase migration, change 'events' to 'runs'

  // Online: use a transaction to avoid overwriting newer data
  if (navigator.onLine) {
    let updatedRacers = null;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(runRef);
      const serverRacers = snap.exists() ? snap.data()?.racers || [] : [];
      updatedRacers = mutateRacers(serverRacers);
      transaction.update(runRef, { racers: updatedRacers });
    });
    return updatedRacers;
  }

  // Offline: keep queued writes behavior (last-write-wins on reconnect)
  const localRacers = run.racers || [];
  const updatedRacers = mutateRacers(localRacers);
  await setDoc(runRef, { racers: updatedRacers }, { merge: true });
  return updatedRacers;
};
  const getDefaultRunName = () => {
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const year = now.getFullYear();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `Race ${month}/${day}/${year} ${hours}:${minutes}`;
  };
  
  // Function to update the run name:
const handleUpdateRunName = async () => {
  try {
    // TODO: After Firebase migration, change 'events' to 'runs'
    await updateDoc(doc(db, "events", run.id), { name: runNameEdit });
    setEditingRunName(false);
    showToast("Run name updated.");
  } catch (error) {
    console.error("Error updating run name:", error);
    showToast("Failed to update run name.");
  }
};

// Function to update the run track:
const handleUpdateTrack = async () => {
  try {
    const trackValue = trackEdit.trim() || null;
    // TODO: After Firebase migration, change 'events' to 'runs'
    await updateDoc(doc(db, "events", run.id), { track: trackValue });
    setEditingTrack(false);
    setTrackEdit("");
    showToast("Track updated.");
  } catch (error) {
    console.error("Error updating track:", error);
    showToast("Failed to update track.");
  }
};

// Function to duplicate the run:
const handleDuplicateRun = async () => {
  if (!run) return;
  try {
    // Generate a new name (or you could append " (copy)" etc.)
    const newRunName = getDefaultRunName();
    // Create new guest keys for the duplicate run:
    const newGuestAdminKey = generateRandomKey();
    const newGuestViewerKey = generateRandomKey();

    // Prepare the new racers array by resetting the timing fields
    let newRacers = [];
    if (run.racers && run.racers.length > 0) {
      newRacers = run.racers.map(racer => ({
        racerId: racer.racerId,
        startTime: null,
        finishTime: null,
        duration: null,
      }));
    }

    // Build new run object based on the current run.
    // Remove any unwanted fields (like id) and update timestamps/keys.
    const newRun = {
      ...run,
      name: newRunName,
      createdAt: Date.now(),
      userId: user.uid, // new run owner remains the current user
      admins: [user.uid],
      guestAdminKey: newGuestAdminKey,
      guestViewerKey: newGuestViewerKey,
      racers: newRacers,
    };
    delete newRun.id; // Remove the id field if it exists

    // TODO: After Firebase migration, change 'events' to 'runs'
    const docRef = await addDoc(collection(db, "events"), newRun);
    showToast("Run duplicated successfully!");
    // Automatically navigate to the new run
    navigate(`/timer?runId=${docRef.id}`);
  } catch (error) {
    console.error("Error duplicating run:", error);
    showToast("Failed to duplicate run.");
  }
};

  const [runNameInput, setRunNameInput] = useState(getDefaultRunName());
  const [racersMap, setRacersMap] = useState({});
  const [credits, setCredits] = useState(0);
  
  // Determine admin status: guest viewer link should never grant admin UI.
  const isGuestViewer = guestRole === "viewer" || guestRole === "pendingViewer";
  const isAdmin = !isGuestViewer && (guestRole === "admin" || (run?.admins?.includes(user?.uid) || run?.admins?.includes(user?.email)));
  
  const guestSignInAttemptedRef = useRef(false);

  useEffect(() => {
    if (user) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const guestAdminKeyFromUrl = params.get("guestAdminKey");
    const guestViewerKeyFromUrl = params.get("guestViewerKey");
    const hasGuestKey = Boolean(guestAdminKeyFromUrl || guestViewerKeyFromUrl);

    if (hasGuestKey && !guestSignInAttemptedRef.current) {
      guestSignInAttemptedRef.current = true;
      signInAnonymously(auth)
        .then(() => {
          console.log("Signed in anonymously for guest access");
        })
        .catch((error) => {
          guestSignInAttemptedRef.current = false;
          console.error("Anonymous sign-in error:", error);
        });
    }
  }, [location.search, user]);

  // Fetch user credits on mount.
  useEffect(() => {
    if (!user) return;
    const fetchCredits = async () => {
      if (!user) return;
      const userDocRef = doc(db, "users", user.uid);
      const snapshot = await getDoc(userDocRef);
      if (snapshot.exists()) {
        setCredits(snapshot.data().credits || 0);
      }
    };
    fetchCredits();
  }, [user]);

  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Support both eventId (backward compatibility) and runId
    const runId = params.get("runId") || params.get("eventId");
    console.log("TimerPage: runId from URL:", runId);
    if (runId && !run) {
      // TODO: After Firebase migration, change 'events' to 'runs'
      const unsubscribe = onSnapshot(doc(db, "events", runId), (docSnapshot) => {
        if (docSnapshot.exists()) {
          const loadedRun = { id: docSnapshot.id, ...docSnapshot.data() };
          console.log("Run loaded:", loadedRun);
          setRun(loadedRun);
        } else {
          console.error("Run not found");
        }
      });
      return () => unsubscribe();
    }
  }, [location.search, run]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const guestAdminKeyFromUrl = params.get("guestAdminKey");
    const guestViewerKeyFromUrl = params.get("guestViewerKey");
    console.log("Guest keys from URL:", guestAdminKeyFromUrl, guestViewerKeyFromUrl);
    if (guestAdminKeyFromUrl) {
      setGuestRole("pendingAdmin");
    } else if (guestViewerKeyFromUrl) {
      setGuestRole("pendingViewer");
    }
  }, [location.search]);
  
  useEffect(() => {
    if (run) {
      const params = new URLSearchParams(location.search);
      const guestAdminKeyFromUrl = params.get("guestAdminKey");
      const guestViewerKeyFromUrl = params.get("guestViewerKey");
      //console.log("Validating guest keys against run:", guestAdminKeyFromUrl, guestViewerKeyFromUrl, run.guestAdminKey, run.guestViewerKey);
      if (guestAdminKeyFromUrl && guestAdminKeyFromUrl === run.guestAdminKey) {
        setGuestRole("admin");
      } else if (guestViewerKeyFromUrl && guestViewerKeyFromUrl === run.guestViewerKey) {
        setGuestRole("viewer");
      } else {
        setGuestRole(null);
      }
    }
  }, [run, location.search]);
  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch racers map (racerId -> racer name)
  useEffect(() => {
    const racersRef = collection(db, "timer_racers");
    const unsubscribe = onSnapshot(racersRef, (snapshot) => {
      const map = {};
      const racersList = [];
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        console.log("Racer doc:", docSnap.id, data);
        map[docSnap.id] = data.name || "Unknown Racer";
        racersList.push({
          id: docSnap.id,
          name: data.name || "Unknown Racer",
          groups: data.groups || [],
          createdBy: data.createdBy,
        });
      });
      setRacersMap(map);
      setAllRacers(racersList);
    });
    return () => unsubscribe();
  }, []);

  // Fetch all available tracks from events for autocomplete suggestions
  useEffect(() => {
    const eventsRef = collection(db, "events");
    const unsubscribe = onSnapshot(eventsRef, (snapshot) => {
      const tracks = new Set();
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.track && data.track.trim()) {
          tracks.add(data.track.trim());
        }
      });
      setAvailableTracks(Array.from(tracks).sort());
    });
    return () => unsubscribe();
  }, []);

  // Listen for real‑time updates on the active run and reconcile finish/start times
  useEffect(() => {
    let unsubscribe;
    if (run && run.id) {
      // TODO: After Firebase migration, change 'events' to 'runs'
      unsubscribe = onSnapshot(doc(db, 'events', run.id), async (docSnapshot) => {
        const updatedRun = { id: docSnapshot.id, ...docSnapshot.data() };
        let needsUpdate = false;
        
        // Reconcile: If a racer has finishTime but no duration (startTime was missing), 
        // and now has startTime, calculate duration
        if (updatedRun.racers && Array.isArray(updatedRun.racers)) {
          updatedRun.racers = updatedRun.racers.map(racer => {
            if (racer.finishTime && racer.startTime && !racer.duration) {
              // Start time just synced, calculate duration now
              const duration = racer.finishTime - racer.startTime;
              needsUpdate = true;
              return { ...racer, duration };
            }
            return racer;
          });
        }
        
        setRun(updatedRun);
        
        // If duration was calculated, update it in Firestore
        if (needsUpdate) {
          try {
            // TODO: After Firebase migration, change 'events' to 'runs'
            await updateDoc(doc(db, 'events', run.id), {
              racers: updatedRun.racers
            });
            console.log("Duration calculated and saved for reconciled racer");
          } catch (error) {
            console.error("Error saving reconciled duration:", error);
          }
        }
      });
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [run && run.id]);

  useEffect(() => {
    // If we have a run, and a user exists, and the user is anonymous,
    // and the URL contains a valid guestAdminKey, then update the run's admins.
    const params = new URLSearchParams(location.search);
    const guestAdminKeyFromUrl = params.get("guestAdminKey");
    if (
      run &&
      guestAdminKeyFromUrl &&
      guestAdminKeyFromUrl === run.guestAdminKey &&
      user &&
      user.isAnonymous // user is anonymous
    ) {
      if (!run.admins.includes(user.uid)) {
        // TODO: After Firebase migration, change 'events' to 'runs'
        updateDoc(doc(db, "events", run.id), {
          admins: arrayUnion(user.uid),
          guestAdminKey: run.guestAdminKey  
        }).then(() => {
          console.log("Anonymous uid added to run admins.");
        }).catch((error) => {
          console.error("Error adding anonymous uid to run admins:", error);
        });
      }
    }
  }, [run, user, location.search]);

  const handleInviteGuestAdmin = async () => {
    if (!inviteEmail.trim()) return;
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      await updateDoc(doc(db, "events", run.id), {
        admins: arrayUnion(inviteEmail.trim())
      });
      alert("Guest admin invited successfully.");
      setInviteEmail("");
      setShowInviteModal(false);
    } catch (error) {
      console.error("Error inviting guest admin:", error);
    }
  };
  // Create a new run and charge 1 credit.
  const handleCreateRun = async (name) => {
    if (!user) {
      showToast("You must be logged in to create a run.");
      return;
    }
    
    // Prevent anonymous users from creating runs
    if (user.isAnonymous) {
      showToast("Anonymous users cannot create runs. Please sign in to create a run.");
      return;
    }

    if (credits < creditCosts.CREATE_EVENT) {
      showToast("Not enough credits to create a run.");
      return;
    }
    
    try {
      // Check if user document exists before trying to update credits
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (!userDocSnap.exists()) {
        showToast("User account not found. Please contact support.");
        console.error("User document does not exist:", user.uid);
        return;
      }

      // Deduct credit using transaction for atomicity
      console.log("Starting credit deduction transaction...");
      await runTransaction(db, async (transaction) => {
        // Re-fetch user document within transaction to ensure we have latest credits
        const userDoc = await transaction.get(userDocRef);
        if (!userDoc.exists()) {
          throw new Error("User document does not exist");
        }
        
        const currentCredits = userDoc.data().credits || 0;
        console.log("Current credits:", currentCredits, "Required:", creditCosts.CREATE_EVENT);
        if (currentCredits < creditCosts.CREATE_EVENT) {
          throw new Error("Not enough credits");
        }
        
        // Update credits
        transaction.update(userDocRef, { 
          credits: currentCredits - creditCosts.CREATE_EVENT 
        });
      });
      console.log("Credit deduction transaction completed successfully");
      
      // Update local credits state
      setCredits(credits - creditCosts.CREATE_EVENT);
      console.log("Local credits state updated");

      const guestAdminKey = generateRandomKey();
      const guestViewerKey = generateRandomKey();
  
      const newRun = {
        name,
        racers: [],
        createdAt: Date.now(),
        userId: user.uid,
        admins: [user.uid],
        guestAdminKey,  
        guestViewerKey, 
      };
      
      console.log("Creating run with data:", newRun);
      console.log("User UID:", user.uid);
      console.log("User isAnonymous:", user.isAnonymous);
      console.log("User email:", user.email);
      
      // Verify user is authenticated
      if (!user || !user.uid) {
        showToast("User not authenticated. Please log in and try again.");
        return;
      }
      
      // Debug: Try to verify auth state with Firestore by attempting a simple read
      try {
        const testDocRef = doc(db, "users", user.uid);
        const testSnap = await getDoc(testDocRef);
        console.log("Auth verification: Can read user doc:", testSnap.exists());
        if (!testSnap.exists()) {
          showToast("Cannot verify authentication. Please log out and log back in.");
          return;
        }
      } catch (authError) {
        console.error("Auth verification failed:", authError);
        if (authError.code === 'permission-denied') {
          showToast("Authentication error. Please log out and log back in.");
          return;
        }
      }
      
      // Create run document
      let docRef;
      try {
        console.log("Attempting to create run document...");
        console.log("Run data being sent:", JSON.stringify(newRun, null, 2));
        // TODO: After Firebase migration, change 'events' to 'runs'
        docRef = await addDoc(collection(db, 'events'), newRun);
        console.log("Run created successfully with ID:", docRef.id);
      } catch (runError) {
        // If run creation fails, rollback the credit deduction
        console.error("Error creating run document:", runError);
        console.error("Error code:", runError.code);
        console.error("Error message:", runError.message);
        
        try {
          await runTransaction(db, async (transaction) => {
            const userDoc = await transaction.get(userDocRef);
            if (userDoc.exists()) {
              const currentCredits = userDoc.data().credits || 0;
              // Rollback: add back the credits we deducted
              transaction.update(userDocRef, { 
                credits: currentCredits + creditCosts.CREATE_EVENT 
              });
            }
          });
          setCredits(credits); // Restore local state
          console.log("Credits rollback completed");
        } catch (rollbackError) {
          console.error("Error rolling back credits:", rollbackError);
        }
        
        // Re-throw with better error message
        const errorCode = runError.code || runError.code_;
        if (errorCode === 'permission-denied' || runError.message?.includes('permission')) {
          showToast("Permission denied. Please check your Firestore security rules in Firebase Console → Firestore Database → Rules. You need to allow authenticated users to create runs.");
          throw new Error("Permission denied: " + (runError.message || "Missing or insufficient permissions"));
        } else {
          showToast(`Failed to create run: ${runError.message || runError.code || 'Unknown error'}`);
          throw runError;
        }
      }
      
      // Success - run created and credits deducted
      setRun({ id: docRef.id, ...newRun });
      showToast("Run created successfully!");
      navigate(`/timer?runId=${docRef.id}`);
    } catch (error) {
      console.error("Error creating run:", error);
      console.error("Error code:", error.code);
      console.error("Error message:", error.message);
      console.error("Full error:", JSON.stringify(error, null, 2));
      
      const errorCode = error.code || error.code_;
      if (errorCode === 'permission-denied' || error.message?.includes('permission') || error.message?.includes('Permission denied')) {
        showToast("Permission denied. Please check your Firestore security rules in Firebase Console → Firestore Database → Rules. You need to allow authenticated users to create events.");
      } else if (error.message && error.message.includes("Not enough credits")) {
        showToast("Not enough credits to create a run.");
      } else if (error.message) {
        showToast(`Failed to create run: ${error.message}`);
      } else {
        showToast("Failed to create run. Please check the console for details.");
      }
    }
  };

  // When an event is selected from the modal.
  const handleSelectRun = (selectedRun) => {
    setRun(selectedRun);
    setShowRunModal(false);
  };

  // Add multiple racers to the run at once (prevent duplicates).
// Add multiple racers to the run at once (prevent duplicates).
const addMultipleRacersToRun = async (selectedRacers) => {
  if (!run) return;
  try {
    const updatedRacers = await updateRunRacers((currentRacers) => {
      const existingIds = new Set((currentRacers || []).map((r) => r.racerId));
      const newRacers = selectedRacers
        .filter((racer) => !existingIds.has(racer.id))
        .map((racer) => ({
          racerId: racer.id,
          startTime: null,
          finishTime: null,
          duration: null,
        }));
      if (newRacers.length === 0) return currentRacers || [];
      return [...(currentRacers || []), ...newRacers];
    });
    if (updatedRacers) setRun((prev) => ({ ...prev, racers: updatedRacers }));
  } catch (error) {
    console.error("Error adding racers to run:", error);
  }
};

  // Remove a racer from the event.
  const removeRacerFromRun = async (racerId) => {
    try {
      const updatedRacers = await updateRunRacers((currentRacers) =>
        (currentRacers || []).filter((racer) => racer.racerId !== racerId)
      );
      if (updatedRacers) setRun((prev) => ({ ...prev, racers: updatedRacers }));
    } catch (error) {
      console.error("Error removing racer from run:", error);
    }
  };

  // Record start time using a transaction.

  // Action to record start time
  const handleStart = async (racerId) => {
    if (!run || !run.id) return;
    if (processingRacerIds.includes(racerId)) return;
    if (user && user.isAnonymous && !run.admins.includes(user.uid)) {
      alert("Guest admin access is still initializing. Please try again in a moment.");
      return;
    }
    const startTime = Date.now();
  
    // Optimistically update local state immediately.
    const optimisticRun = { ...run };
    optimisticRun.racers = (optimisticRun.racers || []).map(racer =>
      racer.racerId === racerId && !racer.startTime ? { ...racer, startTime } : racer
    );
    setRun(optimisticRun);
  
    markRacerProcessing(racerId);
  
    try {
      const updatedRacers = await updateRunRacers((currentRacers) =>
        (currentRacers || []).map((racer) => {
          if (racer.racerId !== racerId) return racer;
          if (racer.startTime) return racer; // don't overwrite
          const next = { ...racer, startTime };
          if (next.startTime && next.finishTime && !next.duration) {
            return { ...next, duration: next.finishTime - next.startTime };
          }
          return next;
        })
      );
      if (updatedRacers) setRun((prev) => ({ ...prev, racers: updatedRacers }));
    } catch (error) {
      console.error("Error starting race:", error);
      showToast("Failed to start race. Changes may be synced later.");
      // Optionally, revert optimistic update if desired.
    } finally {
      unmarkRacerProcessing(racerId);
    }
  };


const handleFinish = async (racerId, options = {}) => {
  if (!run || !run.id) return;
  if (processingRacerIds.includes(racerId)) return;
  if (user && user.isAnonymous && !run.admins.includes(user.uid)) {
    alert("Guest admin access is still initializing. Please try again in a moment.");
    return;
  }

  const finishTime = typeof options.finishTime === "number" ? options.finishTime : Date.now();
  const racer = (run.racers || []).find(r => r.racerId === racerId);
  const hasStartTime = !!racer?.startTime;

  // Confirmation: finishing before a start was recorded.
  // Important: we capture the finishTime BEFORE showing the confirmation dialog
  // so the recorded time reflects the initial button press, not the confirm click.
  if (!hasStartTime && !racer?.finishTime && !options.skipConfirm) {
    setFinishWithoutStartConfirm({ racerId, finishTime });
    return;
  }
  
  // Allow finishing even without start time - the start time might not have synced yet
  // (e.g., if the starter is offline). Duration will calculate when start time syncs.

  // Optimistically update local state so the UI immediately reflects the finish.
  const updatedRacers = (run.racers || []).map(racer => {
    if (racer.racerId === racerId && !racer.finishTime) {
      // If we have start time, calculate duration. Otherwise, set finish time only.
      // Duration will be calculated when start time syncs.
      if (racer.startTime) {
        return { ...racer, finishTime, duration: finishTime - racer.startTime };
      } else {
        // Offline mode: finish without start time
        return { ...racer, finishTime };
      }
    }
    return racer;
  });
  setRun({ ...run, racers: updatedRacers });

  // Immediately disable the button.
  markRacerProcessing(racerId);

  try {
    const savedRacers = await updateRunRacers((currentRacers) =>
      (currentRacers || []).map((racer) => {
        if (racer.racerId !== racerId) return racer;
        if (racer.finishTime) return racer; // don't overwrite
        if (racer.startTime) {
          return { ...racer, finishTime, duration: finishTime - racer.startTime };
        }
        return { ...racer, finishTime };
      })
    );
    if (savedRacers) setRun((prev) => ({ ...prev, racers: savedRacers }));
    
    if (hasStartTime) {
      showToast("Race finished!"); 
    } else {
      showToast("Finish time recorded. Duration will calculate when start time syncs.");
    }
  } catch (error) {
    console.error("Error finishing race:", error);
    showToast("Failed to finish race. Changes will sync when online.");
  } finally {
    unmarkRacerProcessing(racerId);
  }
};

const handleUndoFinish = async (racerId) => {
  if (!run || !run.id) return;
  if (processingRacerIds.includes(racerId)) return;
  if (user && user.isAnonymous && !run.admins.includes(user.uid)) {
    alert("Guest admin access is still initializing. Please try again in a moment.");
    return;
  }

  // Optimistically clear finish time so racer returns to inactive (unless they have a start).
  const updatedRacers = (run.racers || []).map((racer) => {
    if (racer.racerId !== racerId) return racer;
    return { ...racer, finishTime: null, duration: null };
  });
  setRun({ ...run, racers: updatedRacers });

  markRacerProcessing(racerId);
  try {
    const savedRacers = await updateRunRacers((currentRacers) =>
      (currentRacers || []).map((racer) => {
        if (racer.racerId !== racerId) return racer;
        return { ...racer, finishTime: null, duration: null };
      })
    );
    if (savedRacers) setRun((prev) => ({ ...prev, racers: savedRacers }));
    showToast("Finish cleared. Racer moved back to inactive.");
  } catch (error) {
    console.error("Error undoing finish:", error);
    showToast("Failed to undo finish. Changes will sync when online.");
  } finally {
    unmarkRacerProcessing(racerId);
  }
};


  const openSwapModal = (racer) => {
    setSwapTargetRacer(racer);
    setSwapSelectedRacerId(null);
    setSwapSearchTerm("");
    setShowSwapModal(true);
  };

  const closeSwapModal = () => {
    setShowSwapModal(false);
    setSwapTargetRacer(null);
    setSwapSelectedRacerId(null);
    setSwapSearchTerm("");
  };

  const handleConfirmSwap = async () => {
    if (!run || !swapTargetRacer || !swapSelectedRacerId) {
      return;
    }

    if (swapSelectedRacerId === swapTargetRacer.racerId) {
      closeSwapModal();
      return;
    }

    const racerAlreadyInRun = (run.racers || []).some(
      (racer) => racer.racerId === swapSelectedRacerId
    );

    if (racerAlreadyInRun) {
      showToast("Selected racer is already part of the run.");
      return;
    }

    try {
      const updatedRacers = await updateRunRacers((currentRacers) =>
        (currentRacers || []).map((racer) => {
          if (racer.racerId !== swapTargetRacer.racerId) return racer;
          return {
            racerId: swapSelectedRacerId,
            startTime: null,
            finishTime: null,
            duration: null,
          };
        })
      );
      if (updatedRacers) setRun((prev) => (prev ? { ...prev, racers: updatedRacers } : prev));
      showToast("Racer swapped successfully!");
      closeSwapModal();
    } catch (error) {
      console.error("Error swapping racer:", error);
      showToast("Failed to swap racer. Please try again.");
    }
  };

  // --- NEW: Delete run functionality --- //
  const handleDeleteRun = async (options = {}) => {
    if (!run || !run.id) return;
    
    if (!options.skipConfirm) {
      setDeleteRunConfirm({ runId: run.id, runName: run.name });
      return;
    }
    
    try {
      // TODO: After Firebase migration, change 'events' to 'runs'
      await deleteDoc(doc(db, "events", run.id));
      setRun(null);
      navigate("/homepage");
      showToast("Run deleted successfully.");
    } catch (error) {
      console.error("Error deleting run", error);
      showToast("Failed to delete run. Please try again.");
    }
  };

  // Utility functions to format time.
  const formatTimeHMS = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const formatDuration = (ms) => {
    if (ms == null) return "";
    const totalSeconds = ms / 1000;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const hundredths = Math.floor((ms % 1000) / 10);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${hundredths.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}.${hundredths.toString().padStart(2, '0')}`;
    }
  };

  const handleShareToPublic = async () => {
    try {
      // Look up the sharegroup document for this user
      const shareGroupRef = doc(db, "sharegroups", user.uid);
      const shareGroupSnap = await getDoc(shareGroupRef);
      if (!shareGroupSnap.exists()) {
        throw new Error("Sharegroup not found for the current user.");
      }
      const shareGroupData = shareGroupSnap.data();
  
      // Update the run with the sharegroup information
      // TODO: After Firebase migration, change 'events' to 'runs'
      await updateDoc(doc(db, "events", run.id), {
        sharedGroupId: shareGroupSnap.id, // This is the unique sharegroup id
        sharedGroupName: shareGroupData.name // Typically "public"
      });
      showToast("Run shared to your public group successfully!");
    } catch (error) {
      console.error("Error sharing run to public group:", error);
      showToast("Failed to share run. Please try again.");
    }
  };

  // Export run results as CSV.
  const exportCSV = () => {
    if (!run || !run.racers) return;
    
    // Sort racers by duration (fastest to slowest)
    // Racers without finish times (no duration) go to the end
    const sortedRacers = [...run.racers].sort((a, b) => {
      // If both have durations, sort by duration (ascending - fastest first)
      if (a.duration && b.duration) {
        return a.duration - b.duration;
      }
      // If only one has duration, the one with duration comes first
      if (a.duration && !b.duration) return -1;
      if (!a.duration && b.duration) return 1;
      // If neither has duration, maintain original order
      return 0;
    });
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Racer,Start Time,Finish Time,Duration\n";
    
    sortedRacers.forEach(racer => {
      const racerName = racersMap[racer.racerId] || racer.racerId;
      const start = racer.startTime ? formatTimeHMS(racer.startTime) : "";
      const finish = racer.finishTime ? formatTimeHMS(racer.finishTime) : "";
      const dur = racer.duration ? formatDuration(racer.duration) : "";
      csvContent += `"${racerName}","${start}","${finish}","${dur}"\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${run.name.replace(/ /g, "_")}_results.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const swapModalRacers = allRacers
    .filter((racer) => {
      if (!run) return false;
      return racer.createdBy === run.userId;
    })
    .filter((racer) => {
      if (!swapSearchTerm.trim()) return true;
      return racer.name
        .toLowerCase()
        .includes(swapSearchTerm.trim().toLowerCase());
    })
    .filter((racer) => racer.id !== swapTargetRacer?.racerId)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Compute run statistics.
  const runRacers = run && run.racers ? run.racers : [];
  const filteredRunRacers = filterRacersByGroup(runRacers);

  const onCourseRacers = filteredRunRacers
    .filter(r => r.startTime && !r.finishTime)
    .sort((a, b) => a.startTime - b.startTime);

  // Inactive racers: no start time and no finish time
  const inactiveRacers = filteredRunRacers.filter(r => !r.startTime && !r.finishTime);

  // Racers waiting for start time sync: have finish time but no start time (offline finish)
  const pendingSyncRacers = filteredRunRacers.filter(r => r.finishTime && !r.startTime);

  // Finished racers: have both start and finish times (or calculated duration)
  const finishedRacers = filteredRunRacers
    .filter(r => r.startTime && r.finishTime && r.duration != null)
    .sort((a, b) => a.duration - b.duration);


const totalRacers = filteredRunRacers.length;

  return (
    <div style={{ padding: "20px" }}>
      { !run ? (
        <div>
          {/* When no run is selected */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "15px" }}>
            <button 
              onClick={() => setShowRunModal(true)} 
              className="primary-btn" 
              style={{ width: "auto" }}
            >
              Select a Run
            </button>
          </div>
          <div style={{ border: "2px dashed #ccc", borderRadius: "10px", padding: "15px", marginTop: "10px" }}>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <input 
                type="text" 
                value={runNameInput}
                onChange={(e) => setRunNameInput(e.target.value)}
                placeholder="Race Name (e.g., Race 03/15/2025 14:30)"
                style={{ padding: "5px", flex: 1 }}
              />
              <button 
                onClick={() => {
                  if (runNameInput.trim()){
                    handleCreateRun(runNameInput.trim());
                    setRunNameInput(getDefaultRunName());
                  }
                }} 
                className="primary-btn" 
                style={{ width: "auto" }}
              >
                Create Run : {creditCosts.CREATE_EVENT}⚡
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* Run Header */}
          <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
  <button
    onClick={() => navigate(-1)}
    style={{
      background: "none",
      border: "none",
      cursor: "pointer",
      marginRight: "10px",
      fontSize: "1.5em",
      color: "#555",
    }}
    title="Go Back"
  >
    <FaArrowLeft />
  </button>
  <div style={{ flex: 1 }}>
    {editingRunName ? (
      <input
        type="text"
        value={runNameEdit}
        onChange={(e) => setRunNameEdit(e.target.value)}
        onBlur={handleUpdateRunName}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleUpdateRunName();
          }
          if (e.key === "Escape") {
            setEditingRunName(false);
            setRunNameEdit(run.name);
          }
        }}
        style={{ fontSize: "1.5em", padding: "4px", width: "100%" }}
        autoFocus
      />
    ) : (
      <h2
        style={{ margin: 0, cursor: "pointer" }}
        onClick={() => {
          setRunNameEdit(run.name);
          setEditingRunName(true);
        }}
      >
        {run.name}
      </h2>
    )}
    
    {/* Track Field */}
    <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
      <span style={{ fontSize: "0.9em", color: "#666" }}>Track:</span>
      {editingTrack ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
          <input
            type="text"
            list="track-suggestions"
            value={trackEdit}
            onChange={(e) => setTrackEdit(e.target.value)}
            onBlur={handleUpdateTrack}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleUpdateTrack();
              }
              if (e.key === "Escape") {
                setEditingTrack(false);
                setTrackEdit(run.track || "");
              }
            }}
            placeholder="Enter track name..."
            style={{ 
              fontSize: "0.9em", 
              padding: "4px 8px", 
              border: "1px solid #13A1A8",
              borderRadius: "3px",
              flex: 1,
              maxWidth: "300px"
            }}
            autoFocus
          />
          <datalist id="track-suggestions">
            {availableTracks.map((track) => (
              <option key={track} value={track} />
            ))}
          </datalist>
          <button
            onClick={handleUpdateTrack}
            style={{
              background: "#13A1A8",
              color: "white",
              border: "none",
              borderRadius: "3px",
              padding: "4px 12px",
              cursor: "pointer",
              fontSize: "0.85em"
            }}
          >
            Save
          </button>
          <button
            onClick={() => {
              setEditingTrack(false);
              setTrackEdit(run.track || "");
            }}
            style={{
              background: "#999",
              color: "white",
              border: "none",
              borderRadius: "3px",
              padding: "4px 12px",
              cursor: "pointer",
              fontSize: "0.85em"
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <span
          onClick={() => {
            setTrackEdit(run.track || "");
            setEditingTrack(true);
          }}
          style={{
            fontSize: "0.9em",
            color: run.track ? "#333" : "#999",
            cursor: "pointer",
            textDecoration: run.track ? "none" : "underline",
            fontStyle: run.track ? "normal" : "italic"
          }}
          title="Click to edit track"
        >
          {run.track || "Click to add track"}
        </span>
      )}

      {/* Group Filter */}
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{ fontSize: "0.9em", color: "#666" }}>Group:</span>
        <FilterDropdown
          title="Filter by group"
          dropdownTitle="Filter Groups"
          searchPlaceholder="Search groups..."
          emptyText={availableGroups.length === 0 ? "No groups" : "No groups found"}
          options={availableGroups}
          selectedValues={selectedGroups}
          onToggle={toggleGroup}
          onClear={clearSelectedGroups}
          buttonText="Filter"
          activeBackgroundColor="#13A1A8"
          inactiveTextColor="#333"
          minWidth="220px"
          enableSearch
        />
      </div>
    </div>
  </div>
  <div style={{ display: "flex", alignItems: "center" }}>
    {isAdmin && run.userId === user?.uid && (
      <>
        <button
          type="button"
          onClick={handleDuplicateRun}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#555",
            fontSize: "1.1em",
            marginRight: "10px",
          }}
          title="Duplicate Run"
        >
          <FaClone />
        </button>
        <button
          type="button"
          onClick={handleDeleteRun}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#555",
            fontSize: "1.1em",
          }}
          title="Delete Run"
        >
          <FaTrash />
        </button>
      </>
    )}
  </div>
</div>
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />

          {/* Event Statistics */}
          <div style={{ marginBottom: "15px", textAlign: "center" }}>
            <span>Finished: {finishedRacers.length}</span> |{" "}
            <span>On Course: {onCourseRacers.length}</span> |{" "}
            <span>Total: {totalRacers}</span>
          </div>
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />

          {/* On Course Racers List */}
          <div style={{ marginTop: "20px" }}>
            <h3>🏃 Racers On Course</h3>
            {onCourseRacers.length > 0 ? (
              <TransitionGroup component="ul" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {onCourseRacers.map(racer => (
                  <CSSTransition key={racer.racerId} timeout={300} classNames="fade">
                  <li key={racer.racerId} style={{ display: "flex", alignItems: "center", border: "1px solid #ccc", padding: "10px", marginBottom: "5px", borderRadius: "5px" }}>
                    <div style={{ flex: "1" }}>
                      <strong
                        data-racer-id={racer.racerId}
                        onClick={handleOpenRacer}
                        style={{ cursor: "pointer", color: "#13A1A8", textDecoration: "underline" }}
                        title="View racer details"
                      >
                        {racersMap[racer.racerId] || racer.racerId}
                      </strong>
                      <div>
                        {racer.startTime && <span
                        style={{
                          background: "none", 
                          border: "none", 
                          cursor: "pointer", 
                          color: "#555", 
                          fontSize: ".9em",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px"
                        }}          
    ><FaFlag /> {formatTimeHMS(racer.startTime)}</span>}
                        {racer.finishTime && <span> | <FaFlagCheckered /> {formatTimeHMS(racer.finishTime)}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <button 
                        onClick={() => handleFinish(racer.racerId)}
                        style={{ padding: "10px 15px", borderRadius: "8px", backgroundColor: "#FE6C62", color: "#fff", border: "none", cursor: "pointer" }}
                        disabled={!isAdmin || processingRacerIds.includes(racer.racerId)}
                      >
                        <FaFlagCheckered /> Finish
                      </button>
                    </div>
                  </li>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            ) : <p>No racers on course.</p>}
          </div>
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />

          {/* Inactive Racers List with Add Racer Plus Icon */}
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3>🚦 Inactive Racers</h3>
            {isAdmin && (
              <button 
                onClick={() => setShowRacerModal(true)}
                style={{ 
                  background: "#666", 
                  borderRadius: "10px", 
                  padding: "10px",marginRight: "14px", border: "none", cursor: "pointer", fontSize: ".9em", color: "white" }}
                title="Add Racer"
              >
                <FaPlus /> racers
              </button>
            )}
          </div>
          {inactiveRacers.length > 0 ? (
            <TransitionGroup component="ul" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {inactiveRacers.map(racer => (
                <CSSTransition key={racer.racerId} timeout={300} classNames="fade">
                <li key={racer.racerId} style={{ display: "flex", alignItems: "center", border: "1px solid #ccc", padding: "10px", marginBottom: "5px", borderRadius: "5px" }}>
                  <div style={{ flex: "1" }}>
                  {isAdmin && (
                    <button 
                        onClick={() => removeRacerFromRun(racer.racerId)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#555", fontSize: "1.2em" }}
                      >
                        <FaTimes />
                      </button>
                  )}
                    <strong
                      data-racer-id={racer.racerId}
                      onClick={handleOpenRacer}
                      style={{ cursor: "pointer", color: "#13A1A8", textDecoration: "underline" }}
                      title="View racer details"
                    >
                      {racersMap[racer.racerId] || racer.racerId}
                    </strong>
                    <div>
                      {racer.startTime && <span><FaFlag />: {formatTimeHMS(racer.startTime)}</span>}
                      {racer.finishTime && <span> | <FaFlagCheckered />: {formatTimeHMS(racer.finishTime)}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <button 
                      onClick={() => handleStart(racer.racerId)}
                      style={{ padding: "10px 15px", borderRadius: "8px", backgroundColor: "#13A1A8", color: "#fff", border: "none", marginRight: "5px", cursor: "pointer" }}
                      disabled={!isAdmin || processingRacerIds.includes(racer.racerId)}
                    >
                      <FaFlag /> Start
                    </button>
                    {/* Show finish button for inactive racers - allows finishing even if start time hasn't synced yet */}
                    <button 
                      onClick={() => handleFinish(racer.racerId)}
                      style={{ padding: "10px 15px", borderRadius: "8px", backgroundColor: "#FE6C62", color: "#fff", border: "none", cursor: "pointer" }}
                      disabled={!isAdmin || processingRacerIds.includes(racer.racerId)}
                      title={racer.startTime ? "Finish" : "Finish (start time may not have synced yet - duration will calculate when it syncs)"}
                    >
                      <FaFlagCheckered /> Finish
                    </button>
                  </div>
                </li>
                </CSSTransition>
              ))}
              </TransitionGroup>
          ) : <p>No inactive racers.</p>}
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />

          {/* Leaderboard List */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", marginTop: "20px" }}>
            <h3>🏆 Leaderboard</h3>
            <div style={{ position: "relative" }}>
              <button 
                onClick={() => setShowShareDropdown(!showShareDropdown)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#555",fontSize: "1.1em" }}
                title="Share"
              >
                <FaShareAlt />
              </button>
              {showShareDropdown && (
                <div style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  background: "#fff",
                  border: "1px solid #ccc",
                  width: "250px",
                  borderRadius: "4px",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                  zIndex: 100,
                }}>
                  <button 
                    onClick={exportCSV}
                    style={{ padding: "8px", width: "100%", background: "none", border: "none", textAlign: "left", cursor: "pointer" }}
                  >
                    Export CSV
                  </button>
                  <button 
                    onClick={() => {
                      // For example, copy viewer link to clipboard or navigate.
                      navigator.clipboard.writeText(`${window.location.origin}/timer?runId=${run.id}&guestViewerKey=${run.guestViewerKey}`);
                      alert("Viewer link copied to clipboard!");
                    }}
                    style={{ padding: "8px", width: "100%", background: "none", border: "none", textAlign: "left", cursor: "pointer" }}
                  >
                    Viewer Link
                  </button>
                  <button 
                    onClick={handleShareToPublic}
                    style={{ padding: "8px", width: "100%", background: "none", border: "none", textAlign: "left", cursor: "pointer" }}
                  >
                    Share to Public URL
                  </button>
                </div>
              )}
            </div>
          </div>
          <div style={{ marginTop: "20px", padding: "0px"}}>
          {(finishedRacers.length > 0 || pendingSyncRacers.length > 0) ? (
            <TransitionGroup component="ul" style={{ listStyle: "none", padding: 0, margin: 0 }}>
    {/* Completed racers (with duration) */}
    {finishedRacers.map((racer, index) => (
      <CSSTransition key={racer.racerId} timeout={300} classNames="fade">
      <li
  key={racer.racerId}
  style={{ 
    display: "flex", 
    alignItems: "stretch",  // stretch children to full height
    border: "1px solid #ccc", 
    padding: "0px", 
    marginBottom: "5px",
    borderRadius: "5px",
  }}
>
  {/* Left Section: Rank Cartridge */}
  <div
    style={{
      backgroundColor: "#333",  // dark grey
      color: "#fff",            // white text
      width: "45px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      borderRadius: "4px 0 0 4px",
      fontWeight: "bold",
      fontSize: "0.9em",
      margin: 0,       // remove extra margins if any
      padding: "5px 0",// adjust vertical padding if needed
    }}
  >
    #{index + 1}
  </div>
  
  {/* Right Section remains unchanged */}
  <div style={{ flex: 1 }}>
    {/* First Row: Racer's Name and Total Time */}
    <div 
      style={{ 
        display: "flex", 
        margin: "8px",
        justifyContent: "space-between", 
        marginBottom: "4px",
        fontWeight: "bold",
        alignItems: "center",
      }}
    >
      <span
        data-racer-id={racer.racerId}
        onClick={handleOpenRacer}
        style={{ cursor: "pointer", color: "#13A1A8", textDecoration: "underline" }}
        title="View racer details"
      >
        {racersMap[racer.racerId] || racer.racerId}
      </span>
      <span>{formatDuration(racer.duration)}</span>
    </div>
    {/* Second Row: Remove Action and Start/Finish Times */}
    <div 
      style={{ 
        display: "flex",
        margin: "8px", 
        alignItems: "center", 
        opacity: 0.75,
        fontSize: "0.9em",
        padding: 0,
      }}
    >
    {isAdmin && (
      <button 
        onClick={() => removeRacerFromRun(racer.racerId)}
        style={{ 
          background: "none", 
          border: "none", 
          cursor: "pointer", 
          color: "#555", 
          fontSize: ".9em",
          display: "flex",
          alignItems: "center",
          gap: "4px"  // reduces space between the cross and the text
        }}
        title="Remove racer"
      >
        <FaTimes />
        <span>Remove</span>
      </button>
    )}
      {isAdmin && (
        <button 
          onClick={() => openSwapModal(racer)}
          style={{ 
            background: "none", 
            border: "none", 
            cursor: "pointer", 
            color: "#555", 
            fontSize: ".9em",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            marginLeft: "8px"
          }}
          title="Swap racer"
        >
          <FaExchangeAlt />
          <span>Swap</span>
        </button>
      )}
      <span style={{ 
        background: "none", 
    border: "none", 
    cursor: "pointer", 
    color: "#555", 
    fontSize: ".9em",
    display: "flex",
    alignItems: "center",
    gap: "0px",  
marginRight: "6px"
        }}>
        <FaFlag style={{ marginRight: "4px" }}/>
        {formatTimeHMS(racer.startTime)}
      </span>
      <span style={{
        background: "none", 
    border: "none", 
    cursor: "pointer", 
    color: "#555", 
    fontSize: ".9em",
    display: "flex",
    alignItems: "center",
    gap: "0px"  // reduces space between the cross and the text
      }}>
        <FaFlagCheckered style={{ marginRight: "4px" }}/>
        {formatTimeHMS(racer.finishTime)}
      </span>
    </div>
  </div>
</li>
</CSSTransition>
    ))}
    {/* Pending racers (finished but waiting for start time to sync) */}
    {pendingSyncRacers.map((racer, index) => (
      <CSSTransition key={racer.racerId} timeout={300} classNames="fade">
        <li
          key={racer.racerId}
          style={{ 
            display: "flex", 
            alignItems: "stretch",
            border: "1px solid #ffa500", 
            padding: "0px", 
            marginBottom: "5px",
            borderRadius: "5px",
            backgroundColor: "#fff9e6",
          }}
        >
          {/* Left Section: Pending Indicator */}
          <div
            style={{
              backgroundColor: "#ffa500",
              color: "#fff",
              width: "45px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              borderRadius: "4px 0 0 4px",
              fontWeight: "bold",
              fontSize: "0.8em",
              margin: 0,
              padding: "5px 0",
            }}
          >
            ⏳
          </div>
          
          {/* Right Section */}
          <div style={{ flex: 1 }}>
            {/* First Row: Racer's Name and Pending Status */}
            <div 
              style={{ 
                display: "flex", 
                margin: "8px",
                justifyContent: "space-between", 
                marginBottom: "4px",
                fontWeight: "bold",
                alignItems: "center",
              }}
            >
              <span
                data-racer-id={racer.racerId}
                onClick={handleOpenRacer}
                style={{ cursor: "pointer", color: "#13A1A8", textDecoration: "underline" }}
                title="View racer details"
              >
                {racersMap[racer.racerId] || racer.racerId}
              </span>
              <span style={{ color: "#ffa500", fontStyle: "italic" }}>Pending...</span>
            </div>
            {/* Second Row: Remove Action and Finish Time */}
            <div 
              style={{ 
                display: "flex",
                margin: "8px", 
                alignItems: "center", 
                opacity: 0.75,
                fontSize: "0.9em",
                padding: 0,
              }}
            >
              {isAdmin && (
                <button 
                  onClick={() => removeRacerFromRun(racer.racerId)}
                  style={{ 
                    background: "none", 
                    border: "none", 
                    cursor: "pointer", 
                    color: "#555", 
                    fontSize: ".9em",
                    display: "flex",
                    alignItems: "center",
                  gap: "4px"
                  }}
                  title="Remove racer"
                >
                  <FaTimes />
                  <span>Remove</span>
                </button>
              )}
              {isAdmin && (
                <button 
                  onClick={() => openSwapModal(racer)}
                  style={{ 
                    background: "none", 
                    border: "none", 
                    cursor: "pointer", 
                    color: "#555", 
                    fontSize: ".9em",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "8px"
                  }}
                  title="Swap racer"
                >
                  <FaExchangeAlt />
                  <span>Swap</span>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => handleUndoFinish(racer.racerId)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#555",
                    fontSize: ".9em",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    marginLeft: "8px",
                  }}
                  title="Undo finish (move back to inactive)"
                >
                  <FaUndo />
                  <span>Undo</span>
                </button>
              )}
              <span style={{ 
                background: "none", 
                border: "none", 
                cursor: "pointer", 
                color: "#555", 
                fontSize: ".9em",
                display: "flex",
                alignItems: "center",
                gap: "0px",  
                marginRight: "6px"
              }}>
                <FaFlagCheckered style={{ marginRight: "4px" }}/>
                {formatTimeHMS(racer.finishTime)}
              </span>
              <span style={{ marginLeft: "10px", fontStyle: "italic", color: "#ffa500" }}>
                Waiting for start time...
              </span>
            </div>
          </div>
        </li>
      </CSSTransition>
    ))}
    </TransitionGroup>
) : (
  <p>No finished racers.</p>
)}     </div>
          <hr style={{ margin: "20px 0", borderTop: "2px solid #ddd" }} />

          {/* Action Buttons */}
          <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", marginBottom: "20px" }}>
            <button
              disabled={!isAdmin}
              onClick={() => {
                setRun(null);
                navigate("/timer", { replace: true });
                setShowRunModal(true);
              }}
              className="primary-btn"
              style={{ width: "auto", backgroundColor: "#aaa", padding: "10px 15px", borderRadius: "8px", color: "#fff", border: "none", cursor: "pointer" }}
            >
              Change Event
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="primary-btn"
                style={{ width: "auto", backgroundColor: "#13A1A8", padding: "10px 15px", borderRadius: "8px", color: "#fff", border: "none", cursor: "pointer" }}
              >
                Invite Admin
              </button>
            )}
          </div>

          {/* Invite Modal */}
          {showInviteModal && (
            <div
              className="modal-overlay"
              onClick={() => setShowInviteModal(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  width: "90%",
                  maxWidth: "400px",
                  textAlign: "center",
                }}
              >
                <h3>Invite Guest Admin</h3>
                <p>
                  You can invite a guest admin by 
                  <br/>- sharing this <a href={`${window.location.origin}/timer?runId=${run.id}&guestAdminKey=${run.guestAdminKey}`}>link</a>
                  <br/>- or sharing the QR code below.
                </p>
                {run && (
                  <>
                    <QRCodeCanvas
                      value={`${window.location.origin}/timer?runId=${run.id}&guestAdminKey=${run.guestAdminKey}`}
                      size={128}
                    />
              
                  </>
                )}
                <p>- or enter your guest admin email:</p>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter guest admin email"
                  style={{ padding: "8px", width: "80%", marginBottom: "10px" }}
                /><br/><br/>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    onClick={() => setShowInviteModal(false)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#ccc",
                      border: "none",
                      color: "#333",
                      cursor: "pointer",
                      borderRadius: "4px",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInviteGuestAdmin}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#13A1A8",
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                      borderRadius: "4px",
                    }}
                  >
                    Invite by Email
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Run Confirmation Modal */}
          {deleteRunConfirm && (
            <div
              className="modal-overlay"
              onClick={() => setDeleteRunConfirm(null)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  width: "90%",
                  maxWidth: "420px",
                  textAlign: "left",
                }}
              >
                <h3 style={{ marginTop: 0 }}>Delete run?</h3>
                <p style={{ marginTop: 0, color: "#555" }}>
                  You are about to delete <strong>"{deleteRunConfirm.runName}"</strong>.
                  This action cannot be undone.
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                  <button
                    onClick={() => setDeleteRunConfirm(null)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#ccc",
                      border: "none",
                      color: "#333",
                      cursor: "pointer",
                      borderRadius: "4px",
                      flex: 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setDeleteRunConfirm(null);
                      handleDeleteRun({ skipConfirm: true });
                    }}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#FE6C62",
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                      borderRadius: "4px",
                      flex: 1,
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Finish w/o Start Confirmation Modal */}
          {finishWithoutStartConfirm && (
            <div
              className="modal-overlay"
              onClick={() => setFinishWithoutStartConfirm(null)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff",
                  padding: "20px",
                  borderRadius: "8px",
                  width: "90%",
                  maxWidth: "420px",
                  textAlign: "left",
                }}
              >
                <h3 style={{ marginTop: 0 }}>Finish without start?</h3>
                <p style={{ marginTop: 0, color: "#555" }}>
                  You are about to finish{" "}
                  <strong>
                    {racersMap[finishWithoutStartConfirm.racerId] ||
                      finishWithoutStartConfirm.racerId}
                  </strong>{" "}
                  at <strong>{formatTimeHMS(finishWithoutStartConfirm.finishTime)}</strong>,
                  but there is no start time recorded.
                </p>
                <p style={{ color: "#777" }}>
                  If you continue, they will be marked as Pending until the start time syncs.
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                  <button
                    onClick={() => setFinishWithoutStartConfirm(null)}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#ccc",
                      border: "none",
                      color: "#333",
                      cursor: "pointer",
                      borderRadius: "4px",
                      flex: 1,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const payload = finishWithoutStartConfirm;
                      setFinishWithoutStartConfirm(null);
                      handleFinish(payload.racerId, {
                        finishTime: payload.finishTime,
                        skipConfirm: true,
                      });
                    }}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#FE6C62",
                      border: "none",
                      color: "#fff",
                      cursor: "pointer",
                      borderRadius: "4px",
                      flex: 1,
                    }}
                  >
                    Finish anyway
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Racer Modal */}
          {showRacerModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <RacerModal 
                  eventOwnerId={run.userId}
                  eventRacers={run.racers || []}
                  onClose={() => setShowRacerModal(false)}
                  onSelectRacers={(selectedRacers) => {
                    addMultipleRacersToRun(selectedRacers);
                    setShowRacerModal(false);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {showRunModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <RunModal 
              onClose={() => setShowRunModal(false)}
              onSelectRun={handleSelectRun}
            />
          </div>
        </div>
      )}

      {showSwapModal && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeSwapModal}
        >
          <div
            className="modal-content"
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              width: "90%",
              maxWidth: "400px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: "10px" }}>Swap Racer</h3>
            <p style={{ marginTop: 0, marginBottom: "15px", color: "#555" }}>
              Select a racer to replace{" "}
              <strong>
                {swapTargetRacer
                  ? racersMap[swapTargetRacer.racerId] || swapTargetRacer.racerId
                  : ""}
              </strong>
              .
            </p>
            <input
              type="text"
              value={swapSearchTerm}
              onChange={(e) => setSwapSearchTerm(e.target.value)}
              placeholder="Search racers..."
              style={{
                padding: "8px",
                marginBottom: "12px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: "4px",
              }}
            >
              {swapModalRacers.length === 0 ? (
                <p style={{ padding: "12px", color: "#777" }}>
                  No racers available.
                </p>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {swapModalRacers.map((racer) => (
                    <li
                      key={racer.id}
                      style={{
                        borderBottom: "1px solid #f0f0f0",
                        padding: "10px 12px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="radio"
                        name="swapRacer"
                        value={racer.id}
                        checked={swapSelectedRacerId === racer.id}
                        onChange={() => setSwapSelectedRacerId(racer.id)}
                        style={{ marginRight: "10px" }}
                      />
                      <span>{racer.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "15px",
              }}
            >
              <button
                onClick={closeSwapModal}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#ccc",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  color: "#333",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSwap}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#13A1A8",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  color: "#fff",
                }}
                disabled={!swapSelectedRacerId}
              >
                Swap Racer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimerPage;